import React, { useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../lib/firebase';
import { useAuthStore } from './useAuthStore';
import { useUser } from '../../hooks/useUser';
import { FullPageLoader } from '../../components/common/FullPageLoader';
import { clearCachedOrganization } from '../../stores/useOrganizationStore';

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { setUser, setLoading, setRole } = useAuthStore();
  const { user: userData, loading: userLoading } = useUser();

  // Prevent organization fetch during password reset
  const shouldFetchOrg = () => {
    const isPasswordReset = sessionStorage.getItem('passwordResetFlow') === 'true';
    const isResetPage = window.location.href.includes('__/auth/action');
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('mode');

    // Don't fetch if we're in reset flow or on reset page
    if (isPasswordReset || (isResetPage && mode === 'resetPassword')) {
      return false;
    }
    return true;
  };

  useEffect(() => {
    // Check if we're on the reset password page
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('mode');
    const oobCode = params.get('oobCode');

    if (mode === 'resetPassword' && oobCode) {
      // We're on the reset password page with a valid code
      sessionStorage.setItem('passwordResetFlow', 'true');
      // Clear any stale organization data
      clearCachedOrganization();
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);

      if (user) {
        const tokenResult = await user.getIdTokenResult();
        const tokenRole = typeof tokenResult.claims.role === 'string' ? tokenResult.claims.role : null;
        setRole(tokenRole);

        const isPasswordReset = sessionStorage.getItem('passwordResetFlow') === 'true';

        // Handle reset flow — clear org data
        if (isPasswordReset || !shouldFetchOrg()) {
          clearCachedOrganization(user);
        }
      } else {
        // Only clear reset flag if we're not on reset page and not during reset flow
        const isResetPage = window.location.href.includes('__/auth/action');
        const params = new URLSearchParams(window.location.search);
        const mode = params.get('mode');
        const oobCode = params.get('oobCode');

        if (!isResetPage && !(mode === 'resetPassword' && oobCode)) {
          sessionStorage.removeItem('passwordResetFlow');
          clearCachedOrganization();
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [setUser, setLoading, setRole]);

  // Only show full-page loader on the very first load (no user data yet).
  // Do NOT block on re-fetches — that's what caused the page to "reload" on profile click.
  const isSignupPage = window.location.pathname === '/signup';
  if (userLoading && !userData && !isSignupPage) {
    return <FullPageLoader />;
  }

  return <>{children}</>;
};
